import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ChartLineUp, CursorClick, ShoppingCart, CheckCircle, Copy, WhatsappLogo,
  Percent, ArrowClockwise, Users, QrCode, Download, Printer,
} from "@phosphor-icons/react";

export default function LinkStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referral/link-stats");
      setStats(data);
    } catch {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!stats) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(stats.link_url);
      toast.success("Link copiado!");
    } catch {}
  };

  const shareWa = () => {
    const msg = `Olá! Assine o *Acerto Rápido JVO* pelo meu link (você ganha 50% OFF no 2º pagamento e eu também): ${stats.link_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `acerto-rapido-qr-${stats.ref_code}.png`;
    a.click();
    toast.success("QR Code baixado!");
  };

  const printQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>QR Acerto Rápido JVO</title>
      <style>
        body { font-family: Manrope, Arial, sans-serif; text-align: center; padding: 40px; background: #fff; }
        .card { max-width: 500px; margin: 0 auto; padding: 32px; border: 3px solid #1e40af; border-radius: 16px; }
        h1 { color: #1e40af; margin: 0 0 8px; font-size: 28px; }
        .sub { color: #475569; font-size: 14px; margin-bottom: 24px; }
        img { width: 320px; height: 320px; }
        .url { font-family: monospace; font-size: 12px; color: #475569; margin-top: 16px; word-break: break-all; }
        .cta { background: #fbbf24; color: #1e3a8a; padding: 12px 20px; font-weight: bold; border-radius: 8px; margin-top: 20px; display: inline-block; }
      </style></head><body>
        <div class="card">
          <h1>ACERTO RÁPIDO JVO</h1>
          <div class="sub">Escaneie e assine com 50% OFF</div>
          <img src="${dataUrl}" alt="QR" />
          <div class="cta">Aponte a câmera do celular</div>
          <div class="url">${stats.link_url}</div>
          <div style="font-size:11px;color:#94a3b8;margin-top:20px;">Código: ${stats.ref_code}</div>
        </div>
        <script>setTimeout(()=>window.print(), 500);</script>
      </body></html>
    `);
    win.document.close();
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "-"; }
  };

  const kpis = [
    { label: "Cliques (total)", value: stats.clicks_total, sub: `${stats.clicks_30d} nos últimos 30 dias`, Icon: CursorClick, cls: "bg-blue-50 border-blue-200 text-blue-800" },
    { label: "Hoje", value: stats.clicks_today, sub: "cliques hoje", Icon: ArrowClockwise, cls: "bg-slate-50 border-slate-200 text-slate-800" },
    { label: "Checkouts iniciados", value: stats.checkouts_started, sub: `${stats.checkouts_paid} pagos`, Icon: ShoppingCart, cls: "bg-amber-50 border-amber-200 text-amber-800" },
    { label: "Assinaturas ativas", value: stats.total_conversions, sub: `Conversão ${stats.conversion_rate}%`, Icon: CheckCircle, cls: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  ];

  return (
    <Card data-testid="link-stats-card" className="border-2 border-blue-300 bg-gradient-to-br from-blue-50/60 to-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-900" style={{ fontFamily: "Manrope" }}>
          <ChartLineUp size={26} weight="duotone" className="text-blue-700" />
          Dashboard do seu link /assinar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Link + share */}
        <div className="rounded-lg bg-white border-2 border-blue-100 p-3">
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold mb-1.5">Seu link público</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input readOnly value={stats.link_url} data-testid="link-stats-url" className="text-xs font-mono flex-1" />
            <Button data-testid="link-stats-copy" size="sm" variant="outline" onClick={copy} className="gap-1.5">
              <Copy size={14} /> Copiar
            </Button>
            <Button data-testid="link-stats-wa" size="sm" onClick={shareWa} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </Button>
            <Button data-testid="link-stats-qr-toggle" size="sm" variant="outline" onClick={() => setShowQR(!showQR)} className="gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50">
              <QrCode size={14} weight="duotone" /> {showQR ? "Ocultar QR" : "QR Code"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Quem clicar entra na página de assinatura já com seu código <strong>{stats.ref_code}</strong> — se pagar, você ganha 50% OFF (até 3 indicações).
          </p>

          {showQR && (
            <div className="mt-4 rounded-lg bg-gradient-to-br from-blue-50 to-emerald-50 border-2 border-blue-200 p-4 flex flex-col sm:flex-row items-center gap-4" data-testid="link-stats-qr-panel">
              <div ref={qrRef} className="bg-white p-3 rounded-lg border-2 border-blue-300 shrink-0">
                <QRCodeCanvas value={stats.link_url} size={140} level="M" fgColor="#1e3a8a" includeMargin={false} />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="font-extrabold text-blue-900 text-base" style={{ fontFamily: "Manrope" }}>
                  Imprima e cole no seu caminhão
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Adesivo no vidro, na traseira ou no posto de combustível — cliente aponta a câmera e cai direto na sua página de assinatura com <strong>50% OFF</strong>.
                </p>
                <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                  <Button data-testid="link-stats-qr-download" size="sm" variant="outline" onClick={downloadQR} className="gap-1.5">
                    <Download size={14} /> Baixar PNG
                  </Button>
                  <Button data-testid="link-stats-qr-print" size="sm" onClick={printQR} className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5">
                    <Printer size={14} weight="fill" /> Imprimir adesivo
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className={`rounded-lg border-2 p-3 ${k.cls}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold opacity-80">
                <k.Icon size={13} weight="duotone" /> {k.label}
              </div>
              <div className="text-3xl font-extrabold mt-1" style={{ fontFamily: "Manrope" }} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>{k.value}</div>
              <div className="text-[11px] opacity-70 mt-0.5">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Conversion rate bar */}
        <div className="rounded-lg bg-slate-50 border p-3">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-bold text-slate-700 flex items-center gap-1"><Percent size={13} weight="fill" className="text-emerald-600" /> Taxa de conversão</span>
            <span className="font-extrabold text-slate-900">{stats.conversion_rate}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
              style={{ width: `${Math.min(100, stats.conversion_rate)}%` }}
              data-testid="conversion-bar"
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            {stats.clicks_total > 0
              ? `${stats.total_conversions} de ${stats.clicks_total} cliques viraram assinatura`
              : "Sem cliques ainda — compartilhe seu link no WhatsApp pra começar"}
          </p>
        </div>

        {/* Recent conversions */}
        {stats.recent_conversions && stats.recent_conversions.length > 0 && (
          <div>
            <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-1">
              <Users size={13} weight="duotone" /> Últimas indicações
            </div>
            <div className="space-y-1.5">
              {stats.recent_conversions.map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-white border rounded-md px-3 py-2" data-testid={`recent-${i}`}>
                  <div>
                    <div className="font-semibold text-slate-800">{c.name}</div>
                    <div className="text-[10px] text-slate-500">{c.email_masked} · {fmtDate(c.signup_date)}</div>
                  </div>
                  {c.subscription_active ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">🟢 Ativa</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-300">🟡 Trial</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="link-stats-refresh"
          className="w-full gap-1.5"
        >
          <ArrowClockwise size={14} /> {loading ? "Atualizando…" : "Atualizar estatísticas"}
        </Button>
      </CardContent>
    </Card>
  );
}
